import {map, sortBy} from 'lodash';
import {useContext, useMemo, useState, useEffect, useCallback, Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {RouteComponentProps} from 'react-router';
import {useSocketEvent} from 'socket.io-react-hook';

import {sessionApi, userApi} from 'api';
import {CopyText, Icon, Loader, Button, ScreenRecorderStatus, SessionSchedule} from 'components';
import {AGENT_ACTIVE_SESSION_STATUSES} from 'constants/session';
import {UserContext} from 'context';
import {SocketEvent} from 'enums';
import {useSessionReadyState, useSocketEventCallback} from 'hooks';
import {OnlineSession, SessionStatus} from 'interfaces';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, JoinSessionMatchParams, ParticipantRole, UserParticipantRole} from 'types';
import {logger, sessionUtils, userAuthUtils} from 'utils';

import {JoinSessionError} from './JoinSessionError';
import {SessionParticipant} from './SessionParticipant';

const T_KEYS = TRANSLATION_KEYS.AGENT_JOIN_SESSION_PAGE;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON.SESSION;

export const JoinSessionPage = ({match}: RouteComponentProps<JoinSessionMatchParams>) => {
    const {t} = useTranslation();
    const {auth, socket, settings} = useContext(UserContext);
    const [initialized, setInitialized] = useState(false);
    const [error, setError] = useState<ApiError>(null);
    const [session, setSession] = useState<OnlineSession>(null);
    const [links, setLinks] = useState<{[role: string]: string}>(null);
    const [isStartLoading, setIsStartLoading] = useState(false);
    const [unableJoinSession, setUnableJoinSession] = useState(false);
    const {canJoin, hasActiveSession, activeSessionId, validScreenRecorder, sessionMinutesLimitExceeded} = useSessionReadyState();
    const hasOtherActiveSession = useMemo(() => hasActiveSession && activeSessionId !== session?.id,
        [activeSessionId, hasActiveSession, session?.id]);
    const {sendMessage: subscribeOnSession} = useSocketEvent(socket, SocketEvent.SessionSubscribe);
    const localUser = useMemo(() => userAuthUtils.getLocalUser(), []);

    const {sessionId, role} = useMemo(() => match.params, [match]);
    const isStarted = useMemo(() =>
        session?.status === SessionStatus.InSession || session?.status === SessionStatus.OnHold,
    [session?.status]);
    const localParticipant = useMemo(() => sessionUtils.getParticipant(session, {eid: localUser.eid}), [session, localUser]);

    const canManageSession = useMemo(() => {
        if (localParticipant?.role === ParticipantRole.Agent) {
            return true;
        }
        if (!localParticipant && !sessionUtils.getAgentParticipant(session)) {
            if (!settings.featuresAvailability.assistantEnabled) {
                return true;
            }
            return localUser.inSessionRoles.includes(UserParticipantRole.Agent);
        }
        return false;
    }, [localParticipant, localUser, session, settings]);

    const hasAccessByRole = useMemo(() =>
        !session || localParticipant?.role === role || canManageSession || auth.sessionAccessOnly,
    [session, localParticipant, role, auth.sessionAccessOnly, canManageSession]);

    const navigateToSession = useCallback((joinLink?: string) => {
        sessionUtils.goToSession(joinLink);
    }, []);

    const startSessionRequest = useCallback(() => {
        if (auth.sessionAccessOnly) {
            return userApi.startCurrentAppointment();
        }
        return sessionApi.startSession(sessionId);
    }, [auth, sessionId]);

    const startSession = useCallback(async () => {
        try {
            setIsStartLoading(true);
            const response = await startSessionRequest();
            navigateToSession(auth.sessionAccessOnly ? response?.links?.agent : null);
        } catch (e: any) {
            setError(e);
            setIsStartLoading(false);
        }
    }, [auth, startSessionRequest, navigateToSession]);

    const joinSession = useCallback(() => {
        try {
            setIsStartLoading(true);
            sessionUtils.goToAssistantSession({joinLink: auth.sessionAccessOnly ? links?.assistant : null, sessionId});
        } catch (e: any) {
            setError(e);
            setIsStartLoading(false);
        }
    }, [auth, sessionId, links]);

    useSocketEventCallback(socket, SocketEvent.Authenticated, useCallback(() => {
        setInitialized(false);
    }, []));
    useSocketEventCallback(socket, SocketEvent.SessionChanged, useCallback((_session: OnlineSession) => {
        logger.debug(`Received socket event: ${SocketEvent.SessionChanged}`, _session);
        setSession(_session);
    }, []));
    useSocketEventCallback(socket, SocketEvent.SessionDeleted, useCallback((id: string) => {
        logger.debug(`Received socket event: ${SocketEvent.SessionDeleted}`, id);
        setUnableJoinSession(true);
    }, []));
    useSocketEventCallback(socket, SocketEvent.SessionSubscribeSuccess,
        useCallback((response: {session: OnlineSession, links: {[role: string]: string}}) => {
            logger.debug(`Received socket event: ${SocketEvent.SessionSubscribeSuccess}`, response);
            setSession(response.session);
            setLinks(response.links);
        }, []));
    useSocketEventCallback(socket, SocketEvent.SessionSubscribeError, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.SessionSubscribeError}`);
        setUnableJoinSession(true);
    }, []));

    const tryJoinSession = useCallback(async () => {
        if (canManageSession && session && session?.status !== SessionStatus.New && !isStartLoading) {
            const canJoinSession = canJoin && sessionUtils.canJoinSession(session, settings.featuresAvailability.assistantEnabled);
            const isInSession = sessionUtils.isInSession(session);
            setUnableJoinSession(!canJoinSession);
            if (isInSession && AGENT_ACTIVE_SESSION_STATUSES.includes(session.status)) {
                navigateToSession();
            } else if (canJoinSession) {
                try {
                    setIsStartLoading(true);
                    await sessionUtils.joinSession(session, settings.featuresAvailability.assistantEnabled);
                } catch (e: any) {
                    setError(e);
                } finally {
                    setIsStartLoading(false);
                }
            }
        }
    }, [session, isStartLoading, canJoin, canManageSession, navigateToSession, settings.featuresAvailability.assistantEnabled]);

    useEffect(() => {
        if (!initialized && socket?.connected) {
            subscribeOnSession({sessionId, withToken: auth.sessionAccessOnly});
            setInitialized(true);
        }
    }, [initialized, sessionId, socket, auth, subscribeOnSession]);

    useEffect(() => {
        tryJoinSession();
    }, [tryJoinSession]);

    if (!hasAccessByRole || error || unableJoinSession || (hasOtherActiveSession && session)) {
        return (
            <JoinSessionError
                error={error}
                unableJoinSession={unableJoinSession}
                hasOtherActiveSession={hasOtherActiveSession}
                invalidScreenRecorder={session && !validScreenRecorder}
                sessionMinutesLimitExceeded={sessionMinutesLimitExceeded}
            />);
    } else if (isStartLoading || !session) {
        return <Loader svgClassName='tw-max-w-16 tw-max-h-16' />;
    }

    return (
        <UnauthorizedLayout>
            <div className='tw-flex tw-flex-col tw-items-center'>
                <div className='tw-ts-sub-header -md:tw-ts-h3-sub-header -sm:tw-ts-h4-sub-header tw-text-default-dimmed'>
                    {isStarted ? t(T_KEYS.TITLE_STARTED) : t(T_KEYS.TITLE)}
                </div>
                <div className='tw-flex tw-justify-center'>
                    <div className='tw-ts-h5 tw-text-default-dimmed'>{sessionId}</div>
                    <CopyText
                        text={sessionId}
                        iconOnly
                        iconColor='default-dimmed'
                        className='tw-ml-2'
                    />
                </div>
                <div className='tw-relative tw-flex-center tw-my-10 sm:tw-my-16 tw-w-30 tw-h-30'>
                    <Loader />
                    <Icon name='calendar' size='6xl' color='default-dimmed' />
                </div>
                {settings.userSession?.requireScreenRecorder &&
                    <ScreenRecorderStatus
                        className='tw-mb-4 tw-ts-h5-bold'
                        successClassName='tw-text-default-dimmed'
                        errorClassName='tw-text-alert'
                        messageAppearance='right-label'
                    />}
                <div className='tw-mb-4 tw-text-center'>
                    {canManageSession &&
                        <Fragment>
                            <Button
                                color='primary'
                                width='small'
                                rounded
                                className='tw-relative tw-h-16'
                                Icon={<Icon name='video-call' size='3xl' color='alternative' />}
                                disabled={isStartLoading || !canJoin || !socket?.connected}
                                onClick={startSession}
                            />
                            <div className='tw-ts-h2 tw-font-attention tw-mt-2'>{t(T_KEYS.START_SESSION_BTN)}</div>
                        </Fragment>}
                    {!canManageSession && !isStarted &&
                        <div className='tw-ts-h2 tw-font-attention tw-mt-2'>{t(T_KEYS.WAIT_START_SESSION_LABEL)}</div>}
                    {!canManageSession && isStarted &&
                        <div className='tw-ts-h2 tw-font-attention tw-mt-2'>
                            {
                                <Fragment>
                                    <Button
                                        color='primary'
                                        width='small'
                                        rounded
                                        className='tw-relative tw-h-16'
                                        Icon={<Icon name='video-call' size='3xl' color='alternative' />}
                                        disabled={isStartLoading || !canJoin || !socket?.connected}
                                        onClick={joinSession}
                                    />
                                    <div className='tw-ts-h2 tw-font-attention tw-mt-2'>{t(T_KEYS.JOIN_SESSION_BTN)}</div>
                                </Fragment>
                            }
                        </div>}
                    {!canJoin && sessionMinutesLimitExceeded &&
                        <div className='tw-flex tw-ts-h5 tw-text-alert'>
                            {t(T_KEYS_COMMON.LIMITS.IN_SESSION_MINUTES_LIMIT_IS_OVER)}
                        </div>}
                </div>

                {session?.schedule &&
                    <SessionSchedule
                        session={session}
                        timeZone={settings.common.timeZone}
                        dateFormat={settings.common.dateFormat}
                        timeFormat={settings.common.timeFormat}
                        className='tw-flex tw-ts-h5 tw-text-default-dimmed tw-justify-center'
                    />}

                <div className='tw-p-2 tw-border tw-border-default tw-mt-4 tw-w-full tw-text-center'>
                    <div className='tw-ts-h5-bold tw-text-default-dimmed'>
                        {t(T_KEYS_COMMON.LABELS.PARTICIPANTS)}
                    </div>
                    {map(sortBy(session.participants, 'role'), (participant) => (
                        <SessionParticipant key={participant.id} session={session} participant={participant} nameInBold />
                    ))}
                </div>
            </div>
        </UnauthorizedLayout>
    );
};
